import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { RenewalsRoutingModule } from '@sbd-doug-frontend/app/modules/policy/renewals/renewals.routing';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TasksService } from '@sbd-doug-frontend/app/modules/tasks/tasks.service';
import { AdminUser, Team } from '@sbd-doug-frontend/app/modules/users/admin-users/admin-users.types';
import { UserService } from '@sbd-doug-frontend/app/core/user/user.service';
import { TinyMCEModule } from '@sbd-doug-frontend/shared/lib/ui/tinymce/tinymce.module';
import { FileUploadModule } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.module';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FileUploadConfig } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.component';
import { FileUploaderModalComponent } from '@sbd-doug-frontend/app/modules/file-uploader/file-uploader-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { isObject } from 'lodash-es';
import { TaskFile, TaskType } from '@sbd-doug-frontend/shared/lib/types/task-viewer/tasks.types';
import { AssigneeItem } from '@sbd-doug-frontend/shared/lib/types/task-viewer/task-assignee.types';
import { CommonFormFooterComponent } from '@sbd-doug-frontend/shared/lib/shared-components/common-form-footer/common-form-footer.component';
import { TaskDateTimePickerComponent } from '@sbd-doug-frontend/shared/lib/shared-components/task-date-time-picker/task-date-time-picker.component';
import { SharedPipesModule } from '@sbd-doug-frontend/shared/lib/shared-pipes/shared-pipes.module';
import { QuoteRequestSearchInputComponent } from '@sbd-doug-frontend/shared/lib/shared-components/quote-request-search-input/quote-request-search-input.component';
import { QuoteRequestTaskAssociationViewComponent } from '@sbd-doug-frontend/shared/lib/shared-components/quote-request-task-association-view/quote-request-task-association-view.component';
import { Account, AccountUser, AccountUserListItem } from '@sbd-doug-frontend/shared/lib/types/account.types';
import { CIPXSearchableSelectComponent } from '@sbd-doug-frontend/shared/lib/shared-components/searchable-select/searchable-select.component';
import { QUOTE_REQUESTS_LIST_SERVICE_TOKEN } from '@sbd-doug-frontend/shared/lib/shared-services/quote-requests-list.abstract.service';


export interface TaskCreateFormDraft {
  task_type_id?: number | string | null;
  task_label?: string | null;
  task_description?: string | null;
  task_due_date?: Date | string | null;
  task_due_time?: string | null;
  ignore_time?: boolean | null;
  assign_to?: number[];
  team?: number[];
  account_user?: number[];
  task_related_entity?: {
    task_related_entity_type?: string | null;
    task_related_entity_key?: string | number | null;
  } | null;
  qrAssociatedToTask?: any;
  send_secure_message?: boolean | null;
}

import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'sbd-doug-frontend-task-viewer-content-right-content-stage-column-task-create-form',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatIconButton,
    RenewalsRoutingModule,
    TinyMCEModule,
    FileUploadModule,
    CommonFormFooterComponent,
    TaskDateTimePickerComponent,
    SharedPipesModule,
    QuoteRequestSearchInputComponent,
    QuoteRequestTaskAssociationViewComponent,
    CIPXSearchableSelectComponent,
    MatCheckbox,
    MatTooltip,
  ],
  templateUrl: './task-create-form.component.html',
  styleUrl: './task-create-form.component.scss',
})
export class TaskCreateFormComponent implements OnInit, OnChanges {
  @Output() close = new EventEmitter<boolean>();
  @Output() draftChange = new EventEmitter<TaskCreateFormDraft>();
  @Input() headerTitle!: string;
  @Input() isReplying = false;
  @Input() _taskSetService!: any;
  @Input() taskTypeCode?: string | null;
  @Input() initialTaskDraft?: TaskCreateFormDraft | null;
  @Input() showFooter = true;

  _formBuilder = inject(FormBuilder);
  _tasksService = inject(TasksService);
  _userService = inject(UserService);
  _changeDetectorRef = inject(ChangeDetectorRef);
  _cathyService = inject(CathyService);
  _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  _quoteRequestsListService = inject(QUOTE_REQUESTS_LIST_SERVICE_TOKEN);
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  taskForm: FormGroup;
  taskTypes: TaskType[] = [];
  // List of task types to display in the UI (may be filtered based on context)
  visibleTaskTypes: TaskType[] = [];
  adminUsers: AdminUser[] = [];
  teams: Team[] = [];
  typeTaskId = -1;
  queuedAssignees: AssigneeItem[] = [];
  dateTimePicked;
  isScreenSmall: boolean;
  queuedFiles: File[];
  attachFromQuoteRequestFiles: number[] = [];
  qrAssociatedToTask: any = {};
  @Input() selectedAccount!: Account;
  @Input() selectedAccountUserId?: number | null;
  accountUsersList: AccountUserListItem[] = [];
  accountUsersListWithFullNames: any[] = [];
  adminUsersWithFullNames: any[] = [];

  get accountUserOptions(): { id: number | string; label: string }[] {
    return (this.accountUsersList ?? []).map((accountUser) => ({
      id: accountUser.account_user_id,
      label: `${accountUser.account_user_first_name} ${accountUser.account_user_last_name}`,
    }));
  }
  saveButtonText: string = 'Create';
  assigneesMissing = false;
  private _isApplyingDraft = false;

  constructor(public dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskTypeCode']) {
      this.applyTaskTypeContext();
    }

    if (changes['initialTaskDraft'] && this.taskForm) {
      this.applyTaskDraft(this.initialTaskDraft);
    }

    // If the selected Account changes, refresh the users list and try preselecting
    if (changes['selectedAccount']) {
      this.accountUsersList = this.selectedAccount?.account_users ?? [];
      if (this.accountUsersList?.length) {
        this.accountUsersList.sort((a, b) => {
          const nameA = `${a.account_user_first_name} ${a.account_user_last_name}`.toLowerCase();
          const nameB = `${b.account_user_first_name} ${b.account_user_last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      this.updateAccountUsersWithFullNames();
      // If we are composing a message, attempt to preselect on input change
      if ((this.taskTypeCode ?? '').toLowerCase() === 'message') {
        // Only run when the form is built
        if (this.taskForm) {
          this.preselectAccountUserIfProvided();
        }
      }
    }

    // When an external flow updates the intended recipient while the component
    // is already displayed (e.g., clicking "Send Message" on a different user
    // without navigating away), react immediately and preselect them.
    if (changes['selectedAccountUserId'] && !changes['selectedAccountUserId'].firstChange) {
      if ((this.taskTypeCode ?? '').toLowerCase() === 'message' && this.taskForm) {
        this.preselectAccountUserIfProvided();
      }
    }
  }

  private applyTaskTypeContext(): void {
    const isMessage = (this.taskTypeCode ?? '').toLowerCase() === 'message';
    this.saveButtonText = isMessage ? 'Send Message' : 'Create';

    // If lookups are not yet loaded or form not built, defer the rest
    if (!this.taskTypes || this.taskTypes.length === 0 || !this.taskForm) {
      return;
    }

    this.visibleTaskTypes = isMessage ? this.taskTypes.filter((t) => t.task_type_code === 'message') : this.taskTypes;

    const desiredId = isMessage
      ? (this.taskTypes.find((t) => t.task_type_code === 'message')?.task_type_id ?? -1)
      : (this.taskTypes.find((t) => t.task_type_code === 'generic-task')?.task_type_id ?? -1);

    this.typeTaskId = desiredId;

    const ctrl = this.taskForm.get('task_type_id');
    if (ctrl && desiredId !== undefined && desiredId !== null) {
      this.taskForm.patchValue({ task_type_id: desiredId }, { emitEvent: !this.usesExternalDraft });
    }

    // If switching to message with an associated quote request, refresh account users
    if (isMessage && this.qrAssociatedToTask?.quote_request_id) {
      if (typeof this.loadQuoteRequestAndUpdateAccountUsers === 'function') {
        this.loadQuoteRequestAndUpdateAccountUsers();
      }
    }

    if (isMessage) {
      this.preselectAccountUserIfProvided();
    }

    this._changeDetectorRef.detectChanges();
  }

  private preselectAccountUserIfProvided(): void {
    if (!this.selectedAccountUserId) return;
    const list = this.selectedAccount?.account_users?.length
      ? this.selectedAccount.account_users
      : this.accountUsersList;
    if (!list || list.length === 0) return;
    const exists = list.some((u) => +u.account_user_id === +this.selectedAccountUserId);
    if (!exists || !this.taskForm) return;

    const current = this.taskForm.get('account_user')?.value ?? [];
    const next = Array.isArray(current) ? [...current] : [];
    if (!next.includes(+this.selectedAccountUserId)) {
      next.push(+this.selectedAccountUserId);
    }
    this.taskForm.patchValue({ account_user: next }, { emitEvent: true });
    this.buildQueuedAssigneeList('account_user');
    this._changeDetectorRef.detectChanges();
  }

  ngOnInit() {
    const defaultDateTime = new Date();
    defaultDateTime.setHours(defaultDateTime.getHours() + 1);
    this.accountUsersList = this.selectedAccount?.account_users ?? [];

    // Determine initial button caption based on task type
    if (this.taskTypeCode && this.taskTypeCode.toLowerCase() === 'message') {
      this.saveButtonText = 'Send Message';
    } else {
      this.saveButtonText = 'Create';
    }

    this.accountUsersList.sort((a, b) => {
      const nameA = `${a.account_user_first_name} ${a.account_user_last_name}`.toLowerCase();
      const nameB = `${b.account_user_first_name} ${b.account_user_last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    this.taskForm = this._formBuilder.group({
      task_id: new FormControl('', []),
      task_type_id: new FormControl(this.typeTaskId, [Validators.required]),
      task_label: new FormControl('', [Validators.required]),
      task_description: new FormControl('', [Validators.required]),
      task_due_date: new FormControl('', [Validators.required]),
      task_set_id: new FormControl('', []),
      task_due_time: new FormControl('', []),
      ignore_time: new FormControl(false, []),
      assign_to: new FormControl('', []),
      team: [],
      account_user: [
        (this.taskTypeCode ?? '').toLowerCase() === 'message' && this.accountUsersList.length === 1
          ? [this.accountUsersList[0].account_user_id]
          : [],
      ],
      current_admin_assigned: false,
      task_related_entity: new FormControl('', []),
      send_secure_message: new FormControl(false, []),
    });

    this.taskForm.get('task_type_id').valueChanges.subscribe((value) => {
      const selectedType = this.taskTypes.filter((t) => +t.task_type_id === +value)[0];
      if (selectedType) {
        if (selectedType?.create_task_type_default_label)
          this.taskForm.get('task_label').setValue(selectedType.create_task_type_default_label);
        if (selectedType?.create_task_type_default_label)
          this.taskForm.get('task_description').setValue(selectedType.create_task_type_default_description);

        // Update save button caption dynamically based on selected type
        this.saveButtonText = selectedType?.task_type_code === 'message' ? 'Send Message' : 'Create';

        // Check if task type is "message" and quote request is associated
        if (selectedType?.task_type_code === 'message' && this.qrAssociatedToTask?.quote_request_id) {
          this.loadQuoteRequestAndUpdateAccountUsers();
        }
      }
    });

    this.taskForm.valueChanges.subscribe((value) => {
      this.buildQueuedAssigneeList();
      this.buildQueuedAssigneeList('team');
      this.buildQueuedAssigneeList('account_user');
      this.emitDraftChange();
      if (!this.usesExternalDraft) {
        const rawValue = this.taskForm.getRawValue();
        this._taskSetService.newTaskStoredState.set({ ...rawValue, queuedFiles: this.queuedFiles });
      }
      this._changeDetectorRef.detectChanges();
    });

    this._tasksService.getUILookups().subscribe((res) => {
      this.adminUsers = res.admin_users.sort((a, b) => {
        const nameA = `${a.user_first_name} ${a.user_last_name}`.toLowerCase();
        const nameB = `${b.user_first_name} ${b.user_last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      this.updateAdminUsersWithFullNames();
      this.teams = res.teams;
      this.taskTypes = res.task_types
        .filter((t) => t.task_type_stage === 'available')
        .sort((a, b) => a?.task_type_label.localeCompare(b?.task_type_label));
      // Apply context based on current taskTypeCode (supports instant updates)
      this.applyTaskTypeContext();
      const primary_team = this._userService.currentAdmin().teams?.filter((t) => +t.is_primary_team === 1);

      if (this.usesExternalDraft) {
        if (primary_team?.length) {
          this.taskForm.patchValue(
            {
              team: [primary_team[0].team_id],
            },
            { emitEvent: false }
          );
        }
        this.taskForm.patchValue(
          {
            task_type_id: this.typeTaskId,
            assign_to: [+this._userService.currentAdmin().id],
          },
          { emitEvent: false }
        );
        this.applyTaskDraft(this.initialTaskDraft);
      } else if (this._taskSetService.newTaskStoredState() !== null) {
        this.taskForm.patchValue(this._taskSetService.newTaskStoredState(), { emitEvent: false });
        if (this._taskSetService.newTaskStoredState()?.queuedFiles !== null) {
          this.queuedFiles = this._taskSetService.newTaskStoredState()?.queuedFiles;
        }
      } else {
        if (primary_team?.length) {
          this.taskForm.patchValue({
            team: [primary_team[0].team_id],
          });
          this.buildQueuedAssigneeList('team');
        }
        this.taskForm.patchValue({
          task_type_id: this.typeTaskId,
          assign_to: [+this._userService.currentAdmin().id],
        });
      }

      this.buildQueuedAssigneeList('assign_to');
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    this.taskForm.patchValue(
      {
        task_due_date: this.taskForm.get('task_due_date')?.value || defaultDateTime,
        task_due_time: this.taskForm.get('task_due_time')?.value || '12:00',
      },
      { emitEvent: !this.usesExternalDraft }
    );

    this._changeDetectorRef.detectChanges();
  }

  updateAccountUsersWithFullNames() {
    this.accountUsersListWithFullNames = this.accountUsersList
      .filter((u) =>  u?.account_user_status_label && u?.account_user_status_label == 'Active')
      .map((u) => ({
        ...u,
        account_user_full_name: `${u.account_user_first_name} ${u.account_user_last_name}`,
      }));
  }

  updateAdminUsersWithFullNames() {
    this.adminUsersWithFullNames = this.adminUsers.map((u) => ({
      ...u,
      user_full_name: `${u.user_first_name} ${u.user_last_name}`,
    }));
  }

  onAssigneeSelected($event: any, type = 'assign_to') {
    this.buildQueuedAssigneeList(type as any);
    if (this.queuedAssignees?.length > 0) {
      this.assigneesMissing = false;
    }
  }

  private get usesExternalDraft(): boolean {
    return this.initialTaskDraft !== undefined;
  }

  private applyTaskDraft(draft: TaskCreateFormDraft | null | undefined): void {
    if (!draft || !this.taskForm) {
      return;
    }

    if (this.isDraftApplied(draft)) {
      return;
    }

    this._isApplyingDraft = true;
    this.qrAssociatedToTask = draft.qrAssociatedToTask ?? this.qrAssociatedToTask;
    this.taskForm.patchValue(
      {
        task_type_id: draft.task_type_id ?? this.taskForm.get('task_type_id')?.value,
        task_label: draft.task_label ?? this.taskForm.get('task_label')?.value,
        task_description: draft.task_description ?? this.taskForm.get('task_description')?.value,
        task_due_date: draft.task_due_date ?? this.taskForm.get('task_due_date')?.value,
        task_due_time: draft.task_due_time ?? this.taskForm.get('task_due_time')?.value,
        ignore_time: draft.ignore_time ?? this.taskForm.get('ignore_time')?.value,
        assign_to: draft.assign_to ?? this.taskForm.get('assign_to')?.value,
        team: draft.team ?? this.taskForm.get('team')?.value,
        account_user: draft.account_user ?? this.taskForm.get('account_user')?.value,
        task_related_entity: draft.task_related_entity ?? this.taskForm.get('task_related_entity')?.value,
        send_secure_message: draft.send_secure_message ?? this.taskForm.get('send_secure_message')?.value,
      },
      { emitEvent: false }
    );

    if (typeof draft.task_due_date === 'string') {
      this.dateTimePicked = draft.task_due_date;
    }

    this.buildQueuedAssigneeList();
    this.buildQueuedAssigneeList('team');
    this.buildQueuedAssigneeList('account_user');
    this._isApplyingDraft = false;
  }

  private isDraftApplied(draft: TaskCreateFormDraft): boolean {
    const currentValue = this.taskForm.getRawValue();

    return (
      this.isSameValue(draft.task_type_id, currentValue.task_type_id) &&
      this.isSameValue(draft.task_label, currentValue.task_label) &&
      this.isSameValue(draft.task_description, currentValue.task_description) &&
      this.isSameValue(draft.task_due_date, currentValue.task_due_date) &&
      this.isSameValue(draft.task_due_time, currentValue.task_due_time) &&
      this.isSameValue(draft.ignore_time, currentValue.ignore_time) &&
      this.isSameValue(draft.assign_to, currentValue.assign_to) &&
      this.isSameValue(draft.team, currentValue.team) &&
      this.isSameValue(draft.account_user, currentValue.account_user) &&
      this.isSameValue(draft.send_secure_message, currentValue.send_secure_message) &&
      this.isSameValue(draft.task_related_entity, currentValue.task_related_entity) &&
      this.isSameValue(draft.qrAssociatedToTask, this.qrAssociatedToTask?.quote_request_id ? this.qrAssociatedToTask : null)
    );
  }

  private isSameValue(left: unknown, right: unknown): boolean {
    return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
  }

  private emitDraftChange(): void {
    if (this._isApplyingDraft || !this.usesExternalDraft || !this.taskForm) {
      return;
    }

    this.draftChange.emit({
      ...this.taskForm.getRawValue(),
      qrAssociatedToTask: this.qrAssociatedToTask?.quote_request_id ? this.qrAssociatedToTask : null,
    });
  }

  private buildQueuedAssigneeList(type = 'assign_to') {
    switch (type) {
      case 'assign_to':
        const assign_to = this.taskForm.get(type).value ?? [];
        const cleanedQueue = this.queuedAssignees.filter((i) => i.scope_key !== 'xbadminscope');
        this.queuedAssignees = cleanedQueue;
        if (Array.isArray(assign_to))
          assign_to.forEach((element) => {
            const concernedAdminUser = this.adminUsers.find((i) => +i.admin_user_id === +element);
            if (concernedAdminUser) {
              this.queuedAssignees.push({
                scope_key: 'xbadminscope',
                type: 'Admin User',
                id: +concernedAdminUser.admin_user_id,
                name: concernedAdminUser.user_first_name + ' ' + concernedAdminUser.user_last_name,
              });
            }
          });
        break;
      case 'team':
        const team_assigned_to = this.taskForm.get(type).value ?? [];
        const cleanedTeamQueue = this.queuedAssignees.filter((i) => i.scope_key !== 'cipxadminuserteam');
        this.queuedAssignees = cleanedTeamQueue;
        if (Array.isArray(team_assigned_to))
          team_assigned_to.forEach((element) => {
            const concernedTeam = this.teams.find((i) => +i.team_id === +element);
            if (concernedTeam) {
              this.queuedAssignees.push({
                scope_key: 'cipxadminuserteam',
                type: 'Team',
                id: +concernedTeam.team_id,
                name: concernedTeam.team_label,
              });
            }
          });
        break;
      case 'account_user':
        const account_user_assigned_to = this.taskForm.get(type).value ?? [];
        const cleanedAccountUserQueue = this.queuedAssignees.filter((i) => i.scope_key !== 'cipxaccountuser');
        this.queuedAssignees = cleanedAccountUserQueue;
        if (Array.isArray(account_user_assigned_to))
          account_user_assigned_to.forEach((element) => {
            let concernedAccountUser = null;
            if (this.selectedAccount?.account_id) {
              concernedAccountUser = this.selectedAccount.account_users?.find((i) => +i.account_user_id === +element);
            } else if (this.accountUsersList.length > 0) {
              concernedAccountUser = this.accountUsersList?.find((i) => +i.account_user_id === +element);
            }
            if (concernedAccountUser) {
              this.queuedAssignees.push({
                scope_key: 'cipxaccountuser',
                type: 'Account User',
                id: +concernedAccountUser.account_user_id,
                name: concernedAccountUser.account_user_first_name + ' ' + concernedAccountUser.account_user_last_name,
              });
            }
          });
        break;
      case 'me':
        const current_admin_assigned = this.taskForm.get('current_admin_assigned').value;
        if (current_admin_assigned) {
          this.queuedAssignees.push({
            scope_key: 'xbadminscope',
            type: 'Admin User',
            id: +this._userService.currentAdmin().id,
            name: this._userService.currentAdmin().name,
          });
        } else {
          const ix = this.queuedAssignees.findIndex(
            (i) => i.id === +this._userService.currentAdmin().id && i.scope_key === 'xbadminscope'
          );
          if (ix > -1) {
            this.queuedAssignees.splice(ix, 1);
          }
        }
        const admins = this.queuedAssignees.filter((i) => i.scope_key === 'xbadminscope');
        this.taskForm.patchValue({ assign_to: admins.map((i) => i.id) });
        break;
    }
  }

  shouldShowSecureMessageCheckbox() {
    const selectedTypeId = this.taskForm?.get('task_type_id')?.value;
    const selectedType = this.taskTypes.find((t) => +t.task_type_id === +selectedTypeId);
    const isMessage =
      (this.taskTypeCode ?? '').toLowerCase() === 'message' || selectedType?.task_type_code === 'message';

    return isMessage && this.queuedAssignees.some((assignee) => assignee.scope_key === 'cipxaccountuser');
  }

  moveBackToParent() {
    this.close.emit(true);
  }

  toggleQuoteRequestFile($event: any, file: TaskFile) {
    //attachFromQuoteRequestFiles
    if ($event.target.checked) {
      this.attachFromQuoteRequestFiles.push(file.file_id);
    } else {
      const index = this.attachFromQuoteRequestFiles.findIndex((f) => +f === +file.file_id);
      if (index !== -1) this.attachFromQuoteRequestFiles.splice(index, 1);
    }
  }

  getLink(file_id: number) {
    return `${environment.sbdBaseUrl}/admin/files/view/?file_id=${file_id}`;
  }

  submitForm() {
    this.taskForm.markAllAsTouched();
    if (this.taskForm.invalid) {
      return;
    }

    // Enforce assignee selection: a task cannot be created without assignees
    if (!this.queuedAssignees || this.queuedAssignees.length === 0) {
      this.assigneesMissing = true;
      this._cathyService.error('Please select at least one assignee.');
      this._changeDetectorRef.detectChanges();
      return;
    }
    this.assigneesMissing = false;

    let postData = { ...this.taskForm.getRawValue() };
    if (![null, undefined].includes(this.dateTimePicked)) {
      postData = { ...postData, task_due_date: this.dateTimePicked };
    } else {
      this.taskForm.patchValue({
        task_due_time: '23:59',
      });
    }

    postData = {
      ...postData,
      assign_to: this.queuedAssignees,
      task_completion_type_code: 'basic-anyone',
    };

    // Fixing this https://app.asana.com/1/1163961759334914/project/1208959573488251/task/1215719758082534?focus=true
    // When a selected account is available and the task related entity has not
    // been set, default it to the current account.
    const relatedEntity = postData.task_related_entity;
    const hasRelatedEntity =
      isObject(relatedEntity) && !!(relatedEntity as any).task_related_entity_key;
    if (this.selectedAccount?.account_id && !hasRelatedEntity) {
      postData = {
        ...postData,
        task_related_entity: {
          task_related_entity_type: 'cipxaccount',
          task_related_entity_key: this.selectedAccount.account_id,
        },
      };
    }

    //Attaching Selected QRB files in case some were selected
    postData = {
      ...postData,
      attachFromQuoteRequestFiles: this.attachFromQuoteRequestFiles,
      send_secure_message: this.taskForm.get('send_secure_message')?.value,
    };
    if (this.queuedFiles?.length > 0) {
      const formData = new FormData();

      if (this.qrAssociatedToTask?.quote_request_id) {
        formData.append(`task_related_entity[task_related_entity_type]`, 'cipquotereq');
        formData.append(`task_related_entity[task_related_entity_key]`, this.qrAssociatedToTask.quote_request_id);
      }
      // Append each file to FormData
      this.queuedFiles.forEach((file, index) => {
        formData.append(`task_files[${index}]`, file);
      });

      Object.keys(postData).forEach((key) => {
        if (Array.isArray(postData[key]) || isObject(postData[key])) {
          formData.append(key, JSON.stringify(postData[key]));
        } else {
          formData.append(key, postData[key]);
        }
      });
      postData = formData;
    }

    //Create A new task
    this._taskSetService.createTask(postData).subscribe((r) => {
      if (r.success) {
        this._cathyService.success(r.message);
        this._taskSetService.newTaskStoredState.set(null);
        this.close.emit(true);
      }
    });
  }

  openFileUploader() {
    const dialogData = {
      context: 'cipxtask',
      uploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
      defaultUploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
    };

    const dialogRef = this.dialog.open(FileUploaderModalComponent, {
      data: { ...dialogData },
      width: '20vw',
      height: '200pxvw',
      maxWidth: '80vw',
      maxHeight: '70vw',
      minHeight: '200px',
      minWidth: '300px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        (Array.from(result?.data?.queuedFiles) as File[]).forEach((f) => {
          if (!this.queuedFiles) {
            this.queuedFiles = [];
          }
          this.queuedFiles.push(f);
          this._taskSetService.newTaskStoredState.set({
            ...this.taskForm.getRawValue(),
            queuedFiles: this.queuedFiles,
          });
          this._changeDetectorRef.detectChanges();
        });
      }
    });
    return;
  }

  deleteFile(file: TaskFile | number) {
    this.queuedFiles.splice(file as number, 1);
    this._changeDetectorRef.detectChanges();
  }

  setDatePickedDateTime($event: any) {
    this.dateTimePicked = $event;
  }

  appendQRRelatedEntity(data: any) {
    this.qrAssociatedToTask = data;
    this.taskForm.patchValue({
      task_related_entity: {
        task_related_entity_type: 'cipquotereq',
        task_related_entity_key: this.qrAssociatedToTask.quote_request_id,
      },
    });
    if (!this.selectedAccount?.account_id) {
      this.handleQuoteRequestAssociation();
    }
  }

  private handleQuoteRequestAssociation() {
    const selectedTaskType = this.taskTypes.find((t) => +t.task_type_id === +this.taskForm.get('task_type_id').value);
    if (selectedTaskType?.task_type_code === 'message' && this.qrAssociatedToTask?.quote_request_id) {
      this.loadQuoteRequestAndUpdateAccountUsers();
    }
  }

  private loadQuoteRequestAndUpdateAccountUsers() {
    if (!this.qrAssociatedToTask?.quote_request_id) {
      return;
    }

    this._quoteRequestsListService.loadQuoteRequest(this.qrAssociatedToTask.quote_request_id).subscribe({
      next: (loadedQuoteRequest) => {
        if (loadedQuoteRequest?.is_managed) {
          // If the quote request is managed, add the account users from the selected account
          this.accountUsersList = loadedQuoteRequest?.account_account_users ?? [];
          this.accountUsersList.sort((a, b) => {
            const nameA = `${a.account_user_first_name} ${a.account_user_last_name}`.toLowerCase();
            const nameB = `${b.account_user_first_name} ${b.account_user_last_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
          this.updateAccountUsersWithFullNames();
        }
      },
      error: (error) => {
        this._cathyService.error(error);
      },
    });
  }

  formatUri(uri: any) {
    return environment.dougV2BaseUrl + uri;
  }

  onDetachQuoteRequest(): void {
    this.qrAssociatedToTask = {};
    if (!this.selectedAccount?.account_id) {
      this.accountUsersList = [];
      this._changeDetectorRef.detectChanges();
    }
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.account_user_id || index;
  }
}
